import Button from '@rio-cloud/rio-uikit/Button';
import Table, { TableBody, TableColumn, TableHeader, TableHeaderColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import moment from 'moment';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAllowAssetDeleteWithActiveContractsToggle } from '../../../../configuration/featureToggle/toggleHooks';
import { useIsLatamTenant, useIsTestTenant } from '../../../../configuration/login/loginSlice';
import { useBulkGetActiveContractsQuery } from '../../../services/contractsApi';
import type { Asset } from './types';
import { useCheckActiveContractsBulkQuery } from '../../../services/latamContractsApi';

/**
 * Small wrapper to maintain component in two different dialogs: delete and archive
 */
const Wrapper = ({ deletionMode, children }: { deletionMode: boolean; children: ReactNode }) => {
    return (
        <>
            {deletionMode ? (
                <div className='alert alert-danger display-flex flex-column gap-15 margin-top-10'>{children}</div>
            ) : (
                <div>{children}</div>
            )}
        </>
    );
};

interface BulkActiveContractsWarningProps {
    assets: Asset[];
    deletionMode: boolean;
}

export const BulkActiveContractsWarning = ({ assets, deletionMode }: BulkActiveContractsWarningProps) => {
    const assetIds = assets.map(asset => asset.id);
    const isLatamTenant = useIsLatamTenant();
    const { data: activeContracts } = useBulkGetActiveContractsQuery(assetIds, { skip: isLatamTenant });
    const { data: activeContractsLatam } = useCheckActiveContractsBulkQuery(assetIds, { skip: !isLatamTenant });

    const allowAssetDeleteWithActiveContracts = useAllowAssetDeleteWithActiveContractsToggle();
    const isTestTenant = useIsTestTenant();
    const tableRows = isLatamTenant
        ? (activeContractsLatam ?? []).map(({ product_name, subscription_id, period_description, id }) => ({
              key: subscription_id,
              service: `${product_name} ${period_description}`,
              assetId: id,
              assetName: assets.find(asset => asset.id === id)?.name,
          }))
        : (activeContracts ?? []).map(({ product_level_name, ends_at, id }) => ({
              key: `${id}-${product_level_name}`,
              service: product_level_name,
              endDate: moment(ends_at).format('D.M.Y'),
              assetId: id,
              assetName: assets.find(asset => asset.id === id)?.name,
          }));

    if (
        (activeContracts === undefined || activeContracts.length === 0) &&
        (activeContractsLatam === undefined || activeContractsLatam.length === 0)
    ) {
        return null;
    }

    return (
        <Wrapper deletionMode={deletionMode}>
            <div className='display-flex gap-10'>
                <span
                    className={`text-color-${deletionMode ? 'danger' : 'warning'} text-size-h4 rioglyph rioglyph-${
                        deletionMode ? 'error' : 'warning'
                    }-sign`}
                />
                <div>
                    <strong className='text-size-16'>
                        {deletionMode && !allowAssetDeleteWithActiveContracts && (!isTestTenant || isLatamTenant) ? (
                            <FormattedMessage
                                id={'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.title'}
                            />
                        ) : (
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.content.active-contracts.title'}
                            />
                        )}
                    </strong>
                    <div>
                        {deletionMode ? (
                            <FormattedMessage
                                id={'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.hint'}
                            />
                        ) : (
                            <FormattedMessage
                                id={'intl-msg:asset.archive-confirmation-dialog.content.active-contracts.hint'}
                            />
                        )}
                        <div className='margin-y-10'>
                            <Table rowKey='key' border={true} rounded={true} condensed rowSeparatorWidth={0}>
                                <TableHeader>
                                    <TableHeaderColumn columnKey='service'>
                                        <FormattedMessage
                                            id={
                                                'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.service'
                                            }
                                        />
                                    </TableHeaderColumn>
                                    {!isLatamTenant && (
                                        <TableHeaderColumn columnKey='endDate'>
                                            <FormattedMessage
                                                id={
                                                    'intl-msg:asset.delete-confirmation-dialog.content.active-contracts.end-date'
                                                }
                                            />
                                        </TableHeaderColumn>
                                    )}
                                    <TableHeaderColumn columnKey='asset'>Asset</TableHeaderColumn>
                                </TableHeader>
                                <TableBody>
                                    {tableRows.map(row => (
                                        <TableRow key={row.key} id={row.key}>
                                            <TableColumn columnKey='service'>{row.service}</TableColumn>
                                            {!isLatamTenant && (
                                                <TableColumn columnKey='endDate'>{row.endDate}</TableColumn>
                                            )}
                                            <TableColumn columnKey='asset'>
                                                <div title={row.assetId}>{row.assetName}</div>
                                            </TableColumn>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {deletionMode && (
                            <div style={{ display: 'flex', justifyContent: 'right' }}>
                                <Button
                                    bsStyle={Button.PRIMARY}
                                    autoTrackingKey='intl-msg:asset.delete-confirmation-dialog.button.contact-support.bulk'
                                    onClick={() =>
                                        window.open(
                                            encodeURI(
                                                `https://contact-form.rio.cloud/contact?opener=delete asset ${assetIds}`
                                            ),
                                            '_blank'
                                        )
                                    }
                                >
                                    <FormattedMessage
                                        id={'intl-msg:asset.delete-confirmation-dialog.button.contact-support'}
                                    />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};
